var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc bar
 */
import { createRef } from '@byted/light-render';
import { Group, Rect, Line, createText } from '../../element';
import { SeriesModel } from '../../model';
import { getBoxLabelPosition } from '../../utils/textLayout';
import { getPercentOrNumberValue } from '../../utils/math';
import { registerChart } from '../index';
var Waterfall = /** @class */ (function (_super) {
    __extends(Waterfall, _super);
    function Waterfall() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.legendType = 'series';
        _this.type = 'waterfall';
        return _this;
    }
    Waterfall.prototype.getDefaultOption = function (option) {
        return {
            type: 'waterfall',
            name: '',
            upColor: '#ff7762',
            downColor: '#0fb899',
            sumColor: '#878EA8',
            coord: 'cartesian2d',
            xAxis: 0,
            yAxis: 0,
            polar: 0,
            barWidth: '50%',
            encode: {
                x: '',
                y: '',
                color: [
                    option.encode.y,
                    function (value, index, seriesModel) {
                        var currentOption = seriesModel.getOption();
                        return value >= 0 ? currentOption.upColor : currentOption.downColor;
                    },
                ],
            },
            total: {
                label: this.chart.getLocale().waterfall.total,
                color: '#878EA8',
            },
            shapeStyle: {
                fill: null,
                stroke: null,
                lineWidth: 0,
                cursor: 'pointer',
            },
            connectLine: {
                stroke: '#6e848e',
                lineWidth: 1,
                lineDash: [4, 3],
            },
            label: {
                show: false,
                position: 'outside',
                distance: 4,
                fontSize: 12,
                color: null,
            },
            hover: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            },
            selected: {
                shapeStyle: {
                    fill: null,
                    stroke: null,
                    lineWidth: null,
                },
            },
            visible: true,
            showLegend: true,
            isSum: null,
        };
    };
    Waterfall.prototype.beforeRender = function () {
        var _this = this;
        var _a = this.option, isSum = _a.isSum, sumColor = _a.sumColor;
        var _b = this.option.encode, xField = _b.x, yField = _b.y;
        var dataset = this.getDataset();
        var intervalStack = dataset.intervalStack(yField, function (record) { return !(isSum && isSum(record)); });
        var coord = this.getCoord();
        var categoryAxis = coord.getCategoryAxis();
        var valueAxis = coord.getValueAxis();
        var intervalWidth = 1 / categoryAxis.getPixelValue();
        var barWidth = getPercentOrNumberValue(this.option.barWidth, intervalWidth);
        var points = intervalStack.map(function (_a, dataIndex) {
            var start = _a[0], end = _a[1];
            var data = dataset.getRecord(dataIndex);
            var encode = _this._encode.getEncodeData(data, dataIndex);
            var x = categoryAxis.scale.scale(dataIndex);
            var value = data[yField];
            if (isSum && isSum(data)) {
                start = 0;
                end = dataIndex > 0 ? intervalStack[dataIndex - 1][1] : 0;
                value = end;
            }
            var y1 = valueAxis.scale.scale(start);
            var y2 = valueAxis.scale.scale(end);
            return {
                name: data[xField],
                value: value,
                encode: encode,
                color: isSum && isSum(data) ? sumColor : encode.color,
                plotX: x,
                plotY: y2,
                y: [y1, y2],
                visible: !_this.invisibleItems.has(dataIndex),
                selected: _this.selectedItems.has(data),
                shapeRef: createRef(),
            };
        });
        var total = this.option.total;
        if (total) {
            var sum = dataset.sum(yField, function (record) { return !(isSum && isSum(record)); });
            points.push({
                name: total.label,
                value: sum,
                encode: null,
                color: total.color,
                plotX: categoryAxis.scale.scale(intervalStack.length),
                plotY: valueAxis.scale.scale(sum),
                y: [valueAxis.scale.scale(0), valueAxis.scale.scale(sum)],
                visible: true,
                selected: false,
                shapeRef: createRef(),
            });
        }
        this.setState({ points: points, barWidth: barWidth });
    };
    Waterfall.prototype.renderPoints = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('points'),
        });
        var barWidth = this.state.barWidth;
        this.state.points.forEach(function (point) {
            var color = point.color, plotX = point.plotX, _a = point.y, y1 = _a[0], y2 = _a[1], shapeRef = point.shapeRef;
            var shape = new Rect(__assign(__assign({ ref: shapeRef, x: plotX - barWidth / 2, y: y1, width: barWidth, height: y2 - y1, fill: color, stroke: color }, _this.getShapeStyle(_this.option.shapeStyle)), { r: _this.option.shapeStyle.borderRadius, onEvent: function (event) {
                    return _this.handleUIEvent(event, 'point', {
                        point: _this.wrapPoint(point),
                    });
                } }));
            group.add(shape);
        });
        return group;
    };
    Waterfall.prototype.renderLines = function () {
        var group = new Group({
            key: this.getKey('line'),
        });
        var _a = this.state, points = _a.points, barWidth = _a.barWidth;
        for (var i = 0; i < points.length - 1; i++) {
            var point = points[i];
            var nextPoint = points[i + 1];
            group.add(new Line(__assign({ x1: point.plotX + barWidth / 2, x2: nextPoint.plotX - barWidth / 2, y1: point.plotY, y2: point.plotY }, this.getLineStyle(this.option.connectLine))));
        }
        return group;
    };
    Waterfall.prototype.renderLabels = function () {
        var _this = this;
        var group = new Group({
            key: this.getKey('label'),
        });
        var _a = this.option.label, position = _a.position, distance = _a.distance;
        var defaultColor = position.indexOf('inside') !== -1 ? '#ffffff' : '#6E748E';
        this.state.points.forEach(function (point, dataIndex) {
            var value = point.value;
            var bbox = point.shapeRef.current.getBBox();
            var textPosition = getBoxLabelPosition(bbox, position, distance, value > 0 ? 'top' : 'bottom');
            group.add(createText(__assign(__assign(__assign({ text: _this.getDataLabel(dataIndex, _this.option.label) }, textPosition), { fill: defaultColor }), _this.getTextStyle(_this.option.label))));
        });
        return group;
    };
    Waterfall.prototype.render = function () {
        if (!this.visible()) {
            return;
        }
        var group = new Group({
            key: this.getKey('group'),
        });
        group.add(this.renderPoints());
        this.option.connectLine && group.add(this.renderLines());
        if (this.option.label.show) {
            group.add(this.renderLabels());
        }
        return group;
    };
    Waterfall.dependencies = ['coord', 'legend'];
    return Waterfall;
}(SeriesModel));
export default Waterfall;
registerChart('waterfall', Waterfall);
